import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Tooltip, Collapse, List, ListItem, ListItemText,
  Divider, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate } from 'react-router-dom';
import { leadService } from '../services/api';
import { costingApi } from '../services/costingApi';
import CalculateIcon from '@mui/icons-material/Calculate';

const STAGES = [
  { value: 'new', label: 'Новий', color: 'default' },
  { value: 'contact', label: 'Перший контакт', color: 'info' },
  { value: 'negotiation', label: 'Переговори', color: 'primary' },
  { value: 'technical', label: 'Технічна оцінка', color: 'secondary' },
  { value: 'proposal', label: 'Комерційна пропозиція', color: 'warning' },
  { value: 'approval', label: 'Погодження', color: 'warning' },
  { value: 'won', label: 'Виграно', color: 'success' },
  { value: 'lost', label: 'Програно', color: 'error' },
];

const stageColor = (stage) => STAGES.find((s) => s.value === stage)?.color || 'default';
const stageLabel = (stage) => STAGES.find((s) => s.value === stage)?.label || stage;

const EMPTY_FORM = { name: '', contact_person: '', phone: '', email: '', source: '', stage: 'new', notes: '' };

function LeadRow({ lead, onEdit, onDelete, onConvert, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [calcs, setCalcs] = useState([]);
  const [calcsLoading, setCalcsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCalcsLoading(true);
    costingApi.getCalculations({ lead_id: lead.id })
      .then((r) => {
        if (cancelled) return;
        const rows = Array.isArray(r.data) ? r.data : (r.data?.results || []);
        setCalcs(rows);
      })
      .catch(() => { if (!cancelled) setCalcs([]); })
      .finally(() => { if (!cancelled) setCalcsLoading(false); });
    return () => { cancelled = true; };
  }, [open, lead.id]);

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      await leadService.addComment(lead.id, { text: commentText });
      setCommentText('');
      onRefresh();
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (v, d = 2) => (v != null && v !== '' ? Number(v).toFixed(d) : '—');

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell><strong>{lead.name}</strong><br /><Typography variant="caption" color="text.secondary">{lead.contact_person}</Typography></TableCell>
        <TableCell>{lead.phone}<br /><Typography variant="caption">{lead.email}</Typography></TableCell>
        <TableCell><Chip label={stageLabel(lead.stage)} color={stageColor(lead.stage)} size="small" /></TableCell>
        <TableCell>{lead.source}</TableCell>
        <TableCell>{lead.projects_count || 0}</TableCell>
        <TableCell>{new Date(lead.created_at).toLocaleDateString('uk-UA')}</TableCell>
        <TableCell>
          <Tooltip title="Проекти ліда">
            <IconButton size="small" onClick={() => navigate(`/projects?lead=${lead.id}`)}><FolderOpenIcon fontSize="small" /></IconButton>
          </Tooltip>
          {!lead.customer && (
            <Tooltip title="Перенести в журнал замовників">
              <IconButton size="small" color="success" onClick={() => onConvert(lead)}><CheckCircleOutlineIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
          {lead.customer && (
            <Tooltip title="Відкрити в журналі замовників">
              <IconButton size="small" color="success" onClick={() => navigate(`/customers/${lead.customer}`)}>
                <CheckCircleOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={() => onEdit(lead)}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(lead)}><DeleteIcon fontSize="small" /></IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'background.default' }}>
              {lead.notes && <Typography variant="body2" sx={{ mb: 1 }}>{lead.notes}</Typography>}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2">Розрахунки собівартості</Typography>
                <Button
                  size="small"
                  startIcon={<CalculateIcon />}
                  onClick={() => navigate('/costing')}
                >
                  Калькулятор
                </Button>
              </Box>
              {calcsLoading ? (
                <CircularProgress size={20} sx={{ mb: 2 }} />
              ) : calcs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Немає збережених розрахунків. Збережіть результат у калькуляторі собівартості.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Назва</TableCell>
                        <TableCell>ТПА</TableCell>
                        <TableCell align="right">Ціна, грн</TableCell>
                        <TableCell align="right">Собівартість</TableCell>
                        <TableCell align="right">Маржа</TableCell>
                        <TableCell>Дата</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {calcs.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell>
                            {c.name || c.product_detail?.name || `#${c.id}`}
                            {!c.product && (
                              <Chip size="small" label="віртуальний" sx={{ ml: 0.75, height: 18, fontSize: 10 }} variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>{c.machine_detail?.name || '—'}</TableCell>
                          <TableCell align="right">{fmt(c.sale_price)}</TableCell>
                          <TableCell align="right">{fmt(c.unit_cost)}</TableCell>
                          <TableCell align="right">{c.margin_pct != null ? `${fmt(c.margin_pct, 1)}%` : '—'}</TableCell>
                          <TableCell>{c.created_at ? new Date(c.created_at).toLocaleString('uk-UA') : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Typography variant="subtitle2" gutterBottom>Комунікація</Typography>
              <List dense disablePadding>
                {(lead.comments || []).map((c) => (
                  <React.Fragment key={c.id}>
                    <ListItem disableGutters>
                      <ListItemText
                        primary={c.text}
                        secondary={`${c.author_name ? c.author_name + ' · ' : ''}${new Date(c.created_at).toLocaleString('uk-UA')}`}
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField size="small" fullWidth placeholder="Додати коментар..." value={commentText} onChange={(e) => setCommentText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddComment()} />
                <Button variant="contained" size="small" disabled={submitting || !commentText.trim()} onClick={handleAddComment}>
                  {submitting ? <CircularProgress size={16} /> : <ChatBubbleOutlineIcon fontSize="small" />}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const res = await leadService.getAll(params);
      setLeads(res.data);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (editId) {
      await leadService.update(editId, form);
    } else {
      await leadService.create(form);
    }
    setDialogOpen(false);
    setForm(EMPTY_FORM);
    setEditId(null);
    load();
  };

  const handleEdit = (lead) => {
    setForm({ name: lead.name, contact_person: lead.contact_person, phone: lead.phone, email: lead.email, source: lead.source, stage: lead.stage, notes: lead.notes });
    setEditId(lead.id);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    await leadService.remove(deleteTarget.id);
    setDeleteTarget(null);
    load();
  };

  const handleConvert = async () => {
    await leadService.convertToCustomer(convertTarget.id);
    setConvertTarget(null);
    load();
  };

  const displayed = stageFilter ? leads.filter((l) => l.stage === stageFilter) : leads;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Ліди</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Пошук..." value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: 200 }} />
          <TextField select size="small" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} sx={{ width: 180 }} label="Етап">
            <MenuItem value="">Всі етапи</MenuItem>
            {STAGES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm(EMPTY_FORM); setEditId(null); setDialogOpen(true); }}>
            Новий лід
          </Button>
        </Box>
      </Box>

      {/* Funnel chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {STAGES.map((s) => {
          const cnt = leads.filter((l) => l.stage === s.value).length;
          return cnt > 0 ? (
            <Chip key={s.value} label={`${s.label}: ${cnt}`} color={s.color} variant={stageFilter === s.value ? 'filled' : 'outlined'} size="small" onClick={() => setStageFilter(stageFilter === s.value ? '' : s.value)} sx={{ cursor: 'pointer' }} />
          ) : null;
        })}
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Компанія / Контакт</TableCell>
              <TableCell>Телефон / Email</TableCell>
              <TableCell>Етап</TableCell>
              <TableCell>Джерело</TableCell>
              <TableCell>Проекти</TableCell>
              <TableCell>Дата</TableCell>
              <TableCell>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : displayed.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">Немає лідів</TableCell></TableRow>
            ) : displayed.map((lead) => (
              <LeadRow key={lead.id} lead={lead} onEdit={handleEdit} onDelete={setDeleteTarget} onConvert={setConvertTarget} onRefresh={load} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Редагувати лід' : 'Новий лід'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <TextField label="Компанія / Ім'я *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
          <TextField label="Контактна особа" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} fullWidth />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Телефон" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} fullWidth />
            <TextField label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} fullWidth />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Джерело" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} fullWidth />
            <TextField select label="Етап" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} fullWidth>
              {STAGES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
          </Box>
          <TextField label="Нотатки" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} fullWidth multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>Зберегти</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Видалити лід?</DialogTitle>
        <DialogContent><Typography>Видалити "{deleteTarget?.name}"?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Скасувати</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Видалити</Button>
        </DialogActions>
      </Dialog>

      {/* Convert confirm */}
      <Dialog open={!!convertTarget} onClose={() => setConvertTarget(null)}>
        <DialogTitle>Перенести в журнал замовників?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            «{convertTarget?.name}» буде створено в журналі замовників з контактами з ліда.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Контакт: {convertTarget?.contact_person || '—'} · {convertTarget?.phone || 'без телефону'} · {convertTarget?.email || 'без email'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertTarget(null)}>Скасувати</Button>
          <Button color="success" variant="contained" onClick={handleConvert}>Перенести</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
